<template>
  <div class="show-vendor">
    <LoadingSpinner v-if="isLoading" />

    <div v-else>
      <PageHeader title="Edit Vendor" />

      <div class="shadow p-5 mt-2 mb-5 bg-white rounded">
        <VendorForm
          :model="vendor"
          :save-function="updateVendor"
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import PageHeader from '@/components/app/AppPageHeader.vue'
import { useHead } from '@unhead/vue'
import LoadingSpinner from '@/components/LoadingSpinner.vue'
import { Vendor } from '@/models/vendor'
import VendorForm from '../components/VendorForm.vue'
import { useRoute, useRouter } from 'vue-router'
import { useQuery, useQueryClient, useMutation } from '@tanstack/vue-query'
import VendorService from '@/services/VendorService'
import { useNotification } from '@kyvg/vue3-notification'

useHead({ title: 'Edit Vendor' })

const { notify } = useNotification()
const $router = useRouter()
const $route = useRoute()
const vendorId = $route.params.id as string || ''
const vendorService = new VendorService()

const { data: vendor, isLoading } = useQuery({
  queryKey: ['vendor', vendorId],
  queryFn: () => vendorService.find(vendorId)
})

const queryClient = useQueryClient()
const { mutateAsync: updateVendor } = useMutation({
  mutationFn: (vendor: Vendor) => vendorService.update(vendorId, vendor),
  onSuccess: (updatedVendor: Vendor) => {
    queryClient.setQueryData(['vendor', vendorId], updatedVendor)
    queryClient.invalidateQueries({ queryKey: ['vendors'] })
    notify({
      type: 'success',
      title: 'Vendor updated',
      text: 'Vendor updated successfully'
    })
    $router.push({ name: 'ShowVendor', params: { id: updatedVendor.id } })
  },
  onError: (error) => {
    notify({
      type: 'error',
      title: 'Error',
      text: error.message || 'An error occurred while trying to update the vendor'
    })
  }
})

</script>
